import React, { useCallback, useContext, useEffect, useRef } from 'react';
import Share from 'components/Dashboard/components/Share';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import FeedbackOnItem from 'components/Dashboard/FeedbackItem/FeedbackOnItem';
import TextSelector from 'components/Common/TextSelector';
import getAppSite from 'utils/getAppSite';
import time from 'utils/time';
import adjustPDFScale from 'components/Dashboard/utilities/adjustPDFScale';

import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import RateUpdatesContentButton from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContentButton';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  monthsShort: ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
});
const ShareButtonRender = (props) => {
  const { shareButtonWidget, currentNew, embed } = props;
  const { id, billTitle, title, canonicalUrl, objectId, objectType } = currentNew;
  let item = {
    id,
    title: billTitle ? billTitle : title,
    objectID: objectId,
    type: objectType,
  };
  const ShareOptions = {
    item,
    id: item.id,
    title: item.title,
    shareButtonWidget,
    itemShare: embed,
  };
  if (canonicalUrl) {
    ShareOptions.link = `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}`;
  }
  return <Share {...ShareOptions} />;
};

const LegislationNewContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { search } = state;
  const { Legislation } = contentResults;

  const { activeSearch } = search;
  const {
    currentNew,
    shareButtonWidget,
    contentContainer,
    hideSummary,
    reportWidget,
    embed,
    scrollbarRef,
    scrolling,
    isFromPopup,
    notIncludeBranding,
    notShowShare,
  } = props;

  const { createMarkup } = useCreateMarkup();

  const titleToRender = () => {
    let title;
    if (activeSearch || shareButtonWidget) {
      title = currentNew.billTitle
        ? (currentNew.billTitleHighlighted ?? currentNew.billTitle)
        : (currentNew.titleHighlighted ?? currentNew.title);
    } else {
      title = currentNew.billTitle ?? currentNew.title;
    }
    return title;
  };

  const mainContentContainerRef = useRef(null);

  const titleSection = useRef(null);
  let { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection });

  const alreadyMarkedFeedback = !!currentNew?.feedback;
  const index = Legislation ? Legislation?.hits?.findIndex((item) => item.id === currentNew.id) : null;

  const LegislationContainer = useCallback((node) => {
    if (node !== null) {
      adjustPDFScale(node);
    }
  }, []);

  useEffect(() => {
    const container = mainContentContainerRef.current;

    if (container && getAppSite() === 'usa') {
      const styleTags = container.querySelectorAll('style');
      styleTags.forEach((tag) => tag.parentNode.removeChild(tag));
    }
  }, [reportWidget, currentNew?.id]);

  return (
    <div ref={mainContentContainerRef}>
      <div
        className={`${
          reportWidget ? 'content-title-in-widget' : 'content-title-with-summary'
        } section-title pb-2 legislation-content-title content-border-title`}
        ref={titleSection}
      >
        <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(titleToRender())} />
        <div className='row mx-0 justify-content-between align-items-center'>
          {getAppSite() === 'usa' ? (
            <p className='heading-date flex-grow-1 px-0 mb-0'>
              {currentNew.firstEventDate && (
                <>
                  <span className='date'>
                    Introduced:{' '}
                    {time(currentNew.firstEventDate, { utc: true, skipTimezone: true }).formatSimpleGeneralDate()}
                  </span>
                </>
              )}
              {currentNew.docUrl && (
                <>
                  <span className='dot'></span>
                  <a
                    className='legislation-source legislation-source-size mb-0'
                    href={currentNew.docUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {reportWidget ? 'Source' : 'Go to source'}
                  </a>
                </>
              )}
            </p>
          ) : (
            <>
              {!reportWidget && (
                <a
                  className='legislation-source legislation-source-size mb-0'
                  href={currentNew.url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {'Go to source'}
                </a>
              )}
            </>
          )}
          {!notShowShare && (
            <div className='flex-centered'>
              {!notIncludeBranding && (
                <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
              )}
            </div>
          )}
        </div>
        <div className='my-2'>
          <RateUpdatesContentButton currentNew={currentNew} />
        </div>
      </div>
      {!shareButtonWidget && <ContentSummary content={currentNew} hide={hideSummary} />}
      <div className='px-3 px-lg-4 pb-5 specific-content-container mt-2'>
        {/* FIXED TITLE */}
        <div
          className={`content-item-scrolled-title main-sticky section-title bg-white ${
            showFixedTitle && !isFromPopup && !reportWidget ? 'item-scrolled-title-visible' : ''
          }`}
          style={titleStyles}
        >
          <div className='d-flex justify-content-between scrolled-title-container'>
            <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(titleToRender())} />
            {!notShowShare && (
              <>
                {!notIncludeBranding && (
                  <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} />
                )}
              </>
            )}
          </div>
        </div>
        {/* END FIXED TITLE */}

        {alreadyMarkedFeedback && (
          <FeedbackOnItem
            contentPage={true}
            item={{ objectID: currentNew?.objectId }}
            feedback={currentNew?.feedback}
            keywordsFromLists={currentNew?.keywordsFromLists}
            index={index}
          />
        )}

        <TextSelector
          currentNew={currentNew}
          scrollbarRef={scrollbarRef}
          scrolling={scrolling}
          reportWidget={reportWidget}
        >
          <div
            className={`py-3 pr-3 news-content-in legislation-content-in context update-legislation ${
              getAppSite() === 'usa' ? 'legislation-us-content' : ''
            }`}
          >
            {currentNew?.extract ? (
              <>
                <p dangerouslySetInnerHTML={createMarkup(currentNew.extract)}></p>
              </>
            ) : (
              <>
                {currentNew.lastEvents && (
                  <div className='row mx-0'>
                    <div className='pl-0'>
                      {!!currentNew.session && (
                        <>
                          <h3>Session</h3>
                          <p>{currentNew.session}</p>
                        </>
                      )}

                      <h3>Type of Bill</h3>
                      <p>{currentNew.typeOfBill ? currentNew.typeOfBill : 'No content'}</p>

                      <h3>Summary</h3>
                      <p dangerouslySetInnerHTML={createMarkup(currentNew.summary)}></p>
                      {/*{currentNew.summary?currentNew.summary:'No content'}*/}

                      <h3>Sponsor</h3>
                      <p>{currentNew.sponsor ? currentNew.sponsor : 'No content'}</p>
                      {currentNew.nextEventName && (
                        <>
                          <h3>Current stage</h3>
                          <p>{currentNew.nextEventName ? currentNew.nextEventName : 'No content'}</p>
                        </>
                      )}
                      <h3>Previous events</h3>
                      <ul className='policy-list px-0 primary-legislation-list mb-3'>
                        {currentNew.lastEvents
                          .sort((a, b) => a.order - b.order)
                          .map((item, index) => (
                            <li className='py-2' key={`item${index}`}>
                              {item.name}
                            </li>
                          ))}
                      </ul>

                      <a
                        className='legislation-source'
                        href={currentNew.allBillDocumentsUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        View all documents, including amendments, tracked changes to the Bill, briefing papers, etc.
                      </a>
                    </div>
                  </div>
                )}
                {currentNew.lastEvent && (
                  <div className='row mx-0'>
                    <div className='pl-0'>
                      {!!currentNew.session && (
                        <>
                          <h3>Session</h3>
                          <p>{currentNew.session}</p>
                        </>
                      )}

                      {!!currentNew.description && (
                        <>
                          <h3>Description</h3>
                          <p>{currentNew.description}</p>
                        </>
                      )}
                      {!!currentNew.sponsors?.length > 0 && (
                        <>
                          <h3>Sponsors</h3>
                          <p>{currentNew.sponsors.join(', ')}</p>
                        </>
                      )}
                      {!!currentNew.lastEvent && currentNew.lastEventDate && (
                        <>
                          <h3>Current stage</h3>
                          <p>
                            {currentNew.lastEvent} (
                            {time(currentNew.lastEventDate, {
                              utc: true,
                              skipTimezone: true,
                            }).formatSimpleGeneralDate()}
                            )
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {currentNew.explanatoryNote ? (
                  <>
                    <h3>{getAppSite() === 'usa' ? 'Text' : 'Explanatory note'}</h3>
                    <div
                      className='explanatory-container'
                      dangerouslySetInnerHTML={createMarkup(currentNew.explanatoryNote)}
                      ref={LegislationContainer}
                    ></div>
                  </>
                ) : (
                  <>
                    {getAppSite() === 'usa' && (
                      <>
                        {currentNew.body ? (
                          <>
                            <h3>Text</h3>
                            <div
                              className='explanatory-container'
                              dangerouslySetInnerHTML={createMarkup(currentNew.body)}
                              ref={LegislationContainer}
                            ></div>
                          </>
                        ) : (
                          <div className='explanatory-container'>Download source document for bill text</div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </TextSelector>
      </div>
    </div>
  );
};

export default LegislationNewContent;
